'use strict';

const libPictProvider = require('pict-provider');

/**
 * Routes whose screen is a record form being filled in. A scan never navigates away from one of these.
 *
 * @type {Array<RegExp>}
 */
const FORM_ROUTE_PATTERNS =
[
	/^#\/PSRS\/[^/]+\/(Create|Edit)(\/|$)/,
];

/**
 * Where a found record opens, per entity. A target returns a hash route, or null to leave the scan to the
 * handlers below (the panels' default opens Sample Details).
 *
 * @type {Record<string, (pRecord: Record<string, any>) => string|null>}
 */
const DEFAULT_TARGETS =
{
	Sample: (pRecord) => (pRecord.IDSample ? `#/Sample/Workspace/${pRecord.IDSample}` : null),
	Location: (pRecord) => (pRecord.IDLocation ? `#/Location/Workspace/${pRecord.IDLocation}` : null),
};

/**
 * Scan to open: a keyboard or camera scan of a record's code opens that record's screen, except while a form
 * is being filled in, where it says so and stays put.
 *
 * Off by default. An app that wants it calls `enable()` after the panels have registered their default scan
 * handler, so this one sees scans first; an open Scan Samples panel or cart still takes scans ahead of it.
 */
class ScanToOpenProvider extends libPictProvider
{
	/**
	 * @param {any} pFable
	 * @param {any} pOptions
	 * @param {string} pServiceHash
	 */
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {any} */
		this.pict;

		/** @type {(() => void)|null} */
		this._removeScanHandler = null;
		this.targets = Object.assign({}, DEFAULT_TARGETS);
	}

	/**
	 * @return {boolean}
	 */
	isEnabled()
	{
		return !!this._removeScanHandler;
	}

	/**
	 * Start opening scanned records.
	 *
	 * @param {Record<string, (pRecord: Record<string, any>) => string|null>} [pTargets] - added to or replacing the default targets
	 * @return {boolean} whether the handler is registered
	 */
	enable(pTargets)
	{
		this.targets = Object.assign({}, DEFAULT_TARGETS, pTargets || {});
		if (this._removeScanHandler)
		{
			return true;
		}
		const tmpQR = this.pict.providers.HeadlightQR;
		if (!tmpQR || (typeof tmpQR.pushScanHandler !== 'function'))
		{
			return false;
		}
		this._removeScanHandler = tmpQR.pushScanHandler(
			{
				Hash: 'scan-to-open',
				Mode: 'GoTo',
				// A bare label code (no entity prefix) is looked up as each target entity in turn, so location labels open too.
				RawEntities: Object.keys(this.targets),
				onScan: (pResolution) => this.handleResolution(pResolution),
			});
		return true;
	}

	/**
	 * Stop opening scanned records.
	 */
	disable()
	{
		if (this._removeScanHandler)
		{
			this._removeScanHandler();
			this._removeScanHandler = null;
		}
	}

	/**
	 * @param {{ Status: string, Entity?: string, Record?: Record<string, any> }} pResolution
	 * @return {string|null} the route a found record opens, or null when this handler doesn't open it
	 */
	getTargetRoute(pResolution)
	{
		if (!pResolution || (pResolution.Status !== 'Found'))
		{
			return null;
		}
		const fTarget = this.targets[pResolution.Entity];
		if (typeof fTarget !== 'function')
		{
			return null;
		}
		return fTarget(pResolution.Record || {}) || null;
	}

	/**
	 * Is a form being filled in? Either the route is a create or edit form, the sample workspace is in edit or
	 * create mode on a sample route, or a document open in the document frames has unsaved work.
	 *
	 * @return {boolean}
	 */
	isEditingForm()
	{
		const tmpHash = this._currentHash();
		if (FORM_ROUTE_PATTERNS.some((pPattern) => pPattern.test(tmpHash)))
		{
			return true;
		}
		const tmpSampleWorkspace = this.pict.providers.SampleWorkspace;
		if (tmpSampleWorkspace && tmpSampleWorkspace.isEditing && /^#\/(Sample\/Workspace|PSRS\/Sample)\//.test(tmpHash))
		{
			return true;
		}
		const tmpFrames = this.pict.providers.DocumentFrameSwitcher;
		return !!(tmpFrames && (typeof tmpFrames.hasUnsavedWork === 'function') && tmpFrames.hasUnsavedWork());
	}

	/**
	 * The scan handler.
	 *
	 * @param {{ Status: string, Entity?: string, Record?: Record<string, any> }} pResolution
	 * @return {boolean} true when the scan was handled here
	 */
	handleResolution(pResolution)
	{
		const tmpUser = this.pict.AppData && this.pict.AppData.CurrentUser;
		if (!tmpUser || !tmpUser.LoggedIn)
		{
			return false;
		}
		const tmpRoute = this.getTargetRoute(pResolution);
		if (!tmpRoute)
		{
			return false;
		}
		if (this.isEditingForm())
		{
			const tmpRecord = pResolution.Record || {};
			this._toast(`Scanned ${tmpRecord.Name || tmpRecord.QRCode || 'a record'}. Save or cancel this form first, then scan it again to open it.`, 'is-warning');
			return true;
		}
		if (this._currentHash() !== tmpRoute)
		{
			this._navigate(tmpRoute);
		}
		return true;
	}

	/**
	 * @return {string}
	 */
	_currentHash()
	{
		return (typeof window !== 'undefined') ? String(window.location.hash || '') : '';
	}

	/**
	 * @param {string} pRoute
	 */
	_navigate(pRoute)
	{
		window.location.hash = pRoute;
	}

	/**
	 * @param {string} pMessage
	 * @param {string} pType
	 */
	_toast(pMessage, pType)
	{
		const tmpPanels = this.pict.views.HeadlightPanels;
		if (tmpPanels && (typeof tmpPanels.toast === 'function'))
		{
			tmpPanels.toast({ message: pMessage, type: pType });
		}
	}
}

module.exports = ScanToOpenProvider;
module.exports.default_configuration =
{
	ProviderIdentifier: 'ScanToOpen',
	AutoInitialize: false,
};
module.exports.FORM_ROUTE_PATTERNS = FORM_ROUTE_PATTERNS;
module.exports.DEFAULT_TARGETS = DEFAULT_TARGETS;
